import {
	SCHEDULE_CHANGE_ACTIONS,
	SCHEDULE_CHANGE_ACTOR_KINDS,
	type ScheduleChangeAction,
	type ScheduleChangeActorKind,
	type ScheduleChangeItem,
	type ScheduleChangesWindowId,
} from "./types";

export const matchesActions = (item: Pick<ScheduleChangeItem, "action">, actions: readonly ScheduleChangeAction[]): boolean => actions.includes(item.action);

export const matchesActorKinds = (item: Pick<ScheduleChangeItem, "actorKind">, kinds: readonly ScheduleChangeActorKind[]): boolean =>
	kinds.includes(item.actorKind);

/**
 * A stored selection read back: the known values it names, in `options` order. Null when it can't be used — not a
 * JSON list of strings, or nothing in it is still an option (a value from an older build) — so the caller starts
 * from its default instead of an empty filter that would hide everything.
 */
export const parseStoredSelection = <T extends string>(raw: string, options: readonly T[]): T[] | null => {
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		// Unreadable is answered by the null below; the caller reports it.
		return null;
	}
	if (!Array.isArray(parsed) || !parsed.every((value) => typeof value === "string")) return null;
	const stored = new Set<string>(parsed);
	const kept = options.filter((option) => stored.has(option));
	return kept.length === 0 ? null : kept;
};

export const ALL_ACTIONS: readonly ScheduleChangeAction[] = SCHEDULE_CHANGE_ACTIONS;
export const ALL_ACTOR_KINDS: readonly ScheduleChangeActorKind[] = SCHEDULE_CHANGE_ACTOR_KINDS;

/** The same choices, whatever their order. */
export const isSameSelection = <T extends string>(first: readonly T[], second: readonly T[]): boolean =>
	first.length === second.length && first.every((value) => second.includes(value));

/** Every option is chosen — the filter narrows nothing. */
export const isEverySelected = <T extends string>(selected: readonly T[], options: readonly T[]): boolean =>
	options.every((option) => selected.includes(option));

/**
 * One option checked or unchecked, the rest kept, in `options` order. The last chosen option can't be unchecked — an
 * empty who or "What changed" would mark nothing and read as "nothing changed" — so that returns the selection as it was.
 */
export const toggleSelection = <T extends string>(options: readonly T[], selected: readonly T[], value: T, checked: boolean): readonly T[] => {
	const next = options.filter((option) => (option === value ? checked : selected.includes(option)));
	return next.length === 0 ? selected : next;
};

/** The Changes tab's filters kept for the session: who, What changed and the time range. */
export type SharedChangeFilters = { who: readonly ScheduleChangeActorKind[]; actions: readonly ScheduleChangeAction[]; windowId: ScheduleChangesWindowId };

/** How many of these filters differ from a new session's — part of the Filters button's "filters are set" count. */
export const countChangedFromDefault = (filters: SharedChangeFilters, defaults: SharedChangeFilters): number =>
	[!isSameSelection(filters.who, defaults.who), !isSameSelection(filters.actions, defaults.actions), filters.windowId !== defaults.windowId].filter(Boolean)
		.length;
